import { useCallback, useEffect, useState } from 'react';
import { ISSUE_CHART_FILTER_KINDS } from 'shared/domain/analytics/issueChartFilters';
import type { ProcessType } from 'shared/domain/process/types/model';
import type { IssueChart, IssueChartFilters } from 'shared/types/analytics';
import { isEmptyArray } from 'shared/utils/array';
import { useProjectId, useProjectTimezone } from 'store/project';
import { useVisibleFieldsByProcess, useVisibleFieldsLoading } from 'store/visibleFields';
import { clearFilterByFieldVisibility } from '../filters';
import { extractFiltersValues, hasSameChartFilterValues } from '../filterValues';
import { useChartFiltersPersistence } from '../useChartFiltersPersistence';
import {
  createIssueChartAvailableFields,
  createIssueChartDefaultFilterValues,
  createIssueChartFilters,
} from './model';
import { useIssueChartFilterLabelSources, useIssueChartFilterOptions } from './useIssueChartFilterOptions';

interface UseIssueChartFiltersInput {
  chart: IssueChart;
  /** Processes the chart shows: what the process filter offers and whose fields the filters follow. */
  processes: ProcessType[];
}

/**
 * Filters of an issue chart: `ISSUE_CHART_FILTER_KINDS` of the chart after
 * the date range, starting from the saved ones. A filter of an issue field
 * hidden in all the processes is cleared. Undefined until the saved filters
 * of the selected project are read.
 */
export function useIssueChartFilters({ chart, processes }: UseIssueChartFiltersInput): IssueChartFilters | undefined {
  const kinds = ISSUE_CHART_FILTER_KINDS[chart];
  const options = useIssueChartFilterOptions(processes);
  const labelSources = useIssueChartFilterLabelSources();
  const projectId = useProjectId();
  const timezone = useProjectTimezone();
  const visibleFieldsByProcess = useVisibleFieldsByProcess();
  const isLoadingVisibleFields = useVisibleFieldsLoading();

  const [filters, setFilters] = useState<IssueChartFilters | undefined>(undefined);
  const defaultValues = useCallback(() => createIssueChartDefaultFilterValues(kinds), [kinds]);
  const { savedFilters } = useChartFiltersPersistence(chart, filters, defaultValues);

  useEffect(() => {
    if (isLoadingVisibleFields || isEmptyArray(processes)) return;
    if (!savedFilters || !projectId || !timezone) {
      // Another project's filters are being read: never build on the previous ones.
      setFilters(undefined);
      return;
    }

    const availableFields = createIssueChartAvailableFields(visibleFieldsByProcess, processes);
    setFilters((previousFilters) => {
      const nextFilters = clearFilterByFieldVisibility(
        availableFields,
        createIssueChartFilters({
          kinds,
          values: extractFiltersValues(previousFilters) ?? savedFilters,
          options,
          labelSources,
          timezone,
          setFilters,
        })
      );
      return hasSameChartFilterValues(previousFilters, nextFilters) ? previousFilters : nextFilters;
    });
  }, [kinds, options, labelSources, processes, savedFilters, projectId, timezone, visibleFieldsByProcess, isLoadingVisibleFields]);

  return filters;
}
